/**
 * Tag Filter Utility
 * Implements requirements 2.8, 9.7: Japanese-only tag display
 * 日本語タグフィルタリング - 英語（ラテン文字のみ）は非表示
 */

/**
 * 日本語タグのみをフィルタリングする関数
 * @param tags - フィルタリング対象のタグ配列
 * @returns 日本語のみのタグ配列
 */
export function filterJapaneseTags(tags: string[]): string[] {
    const japanesePattern = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/;
    const latinOnlyPattern = /^[a-zA-Z\s\-_]+$/;

    return tags.filter(tag => {
        // 空文字や無効な値をスキップ
        if (!tag || typeof tag !== 'string') {
            return false;
        }

        // 日本語文字が含まれている場合は採用
        if (japanesePattern.test(tag)) {
            return true;
        }

        // ラテン文字のみの場合は除外
        if (latinOnlyPattern.test(tag)) {
            return false;
        }

        // その他（数字、記号含む）は採用
        return true;
    });
}

/**
 * 表示用にタグを処理する関数
 * 日本語フィルタリング + 重複除去 + 空文字除去
 * @param tags - 処理対象のタグ配列
 * @returns 表示用に処理されたタグ配列
 */
export function processTagsForDisplay(tags: string[]): string[] {
    if (!Array.isArray(tags)) {
        return [];
    }

    // 日本語フィルタリング
    const japaneseTags = filterJapaneseTags(tags);

    // 重複除去と空文字除去
    const uniqueTags = Array.from(new Set(japaneseTags))
        .filter(tag => tag.trim().length > 0)
        .map(tag => tag.trim());

    return uniqueTags;
}

/**
 * タグが日本語かどうかを判定する関数
 * @param tag - 判定対象のタグ
 * @returns 日本語の場合true
 */
export function isJapaneseTag(tag: string): boolean {
    if (!tag || typeof tag !== 'string') {
        return false;
    }

    const japanesePattern = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/;
    const latinOnlyPattern = /^[a-zA-Z\s\-_]+$/;

    // 日本語文字が含まれている場合
    if (japanesePattern.test(tag)) {
        return true;
    }

    // ラテン文字のみの場合は日本語ではない
    if (latinOnlyPattern.test(tag)) {
        return false;
    }

    // その他（数字、記号含む）は日本語として扱う
    return true;
}

/**
 * タグの表示優先度を計算する関数
 * 日本語タグを優先し、より具体的なタグを上位にする
 * @param tags - ソート対象のタグ配列
 * @returns 優先度順にソートされたタグ配列
 */
export function sortTagsByPriority(tags: string[]): string[] {
    return tags.sort((a, b) => {
        // 日本語タグを優先
        const aIsJapanese = isJapaneseTag(a);
        const bIsJapanese = isJapaneseTag(b);

        if (aIsJapanese && !bIsJapanese) return -1;
        if (!aIsJapanese && bIsJapanese) return 1;

        // 長さで優先度を決定（より具体的なタグを優先）
        return b.length - a.length;
    });
}

/**
 * タグの表示制限を適用する関数
 * @param tags - 制限対象のタグ配列
 * @param maxTags - 最大表示タグ数（デフォルト: 5）
 * @returns 制限が適用されたタグ配列
 */
export function limitTagsForDisplay(tags: string[], maxTags: number = 5): string[] {
    const processedTags = processTagsForDisplay(tags);
    const sortedTags = sortTagsByPriority(processedTags);

    return sortedTags.slice(0, maxTags);
}

export default {
    filterJapaneseTags,
    processTagsForDisplay,
    isJapaneseTag,
    sortTagsByPriority,
    limitTagsForDisplay
};